import React, { useState, useEffect } from "react";
import { getProductHeirarchy, getAllSalesPersons, assignSKUsToSP } from "../services/ApiWorkflow";
import { getCookie } from "../services/DataRequestService";
import Select from "react-select";
import { displayMessage } from "../Utils/helper";
import { stageLayoutStyles, stageFormStyles } from "../components/createworkflow/helper";
import PageHeader from "../components/common/PageHeader";

const pageStyles = {
  wrapper: {
    width: "100%",
    boxSizing: "border-box",
    background: "#f4f3ef",
  },
  card: {
    ...stageLayoutStyles.card,
    marginBottom: 14,
  },
  cardTitle: {
    ...stageFormStyles.sectionTitle,
    marginBottom: 12,
    paddingBottom: 10,
    borderBottom: "1px solid #e6eef4",
  },
  selectWrap: {
    maxWidth: 420,
  },
  selectedNote: {
    marginTop: 10,
    padding: "10px 12px",
    background: "#f6fafd",
    border: "1px solid #e6eef4",
    borderRadius: 8,
    fontSize: 13,
    color: "#032B4E",
    fontFamily: "Poppins, sans-serif",
    fontWeight: 600,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontFamily: "Poppins, sans-serif",
  },
  th: {
    background: "#032B4E",
    color: "#fff",
    padding: "10px 14px",
    fontSize: 13,
    fontWeight: 600,
    textAlign: "left",
    borderRight: "1px solid rgba(255,255,255,0.1)",
  },
  thLast: {
    background: "#032B4E",
    color: "#fff",
    padding: "10px 14px",
    fontSize: 13,
    fontWeight: 600,
    textAlign: "center",
  },
  tableWrap: {
    width: "100%",
    overflowX: "auto",
    borderRadius: 12,
    border: "1px solid #e6eef4",
    boxShadow: "0 3px 12px rgba(12, 60, 84, 0.06)",
    background: "#fff",
  },
  td: {
    padding: "10px 14px",
    fontSize: 13,
    color: "#2c3e50",
    borderBottom: "1px solid #e6eef4",
    borderRight: "1px solid #e6eef4",
    verticalAlign: "middle",
  },
  tdCenter: {
    padding: "10px 14px",
    fontSize: 13,
    color: "#2c3e50",
    borderBottom: "1px solid #e6eef4",
    textAlign: "center",
    verticalAlign: "middle",
  },
  checkbox: {
    width: 18,
    height: 18,
    accentColor: "#032B4E",
    cursor: "pointer",
  },
  saveRow: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 4,
  },
  primaryBtn: {
    padding: "8px 18px",
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "Poppins, sans-serif",
    color: "#fff",
    background: "#E06639",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    boxShadow: "0 3px 10px rgba(224, 102, 57, 0.25)",
    transition: "background 0.15s ease",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
  },
  primaryBtnDisabled: {
    background: "#9ca3af",
    cursor: "not-allowed",
    boxShadow: "none",
  },
  countBadge: {
    background: "rgba(255,255,255,0.25)",
    borderRadius: 999,
    padding: "2px 8px",
    fontSize: 11,
    fontWeight: 600,
  },
  emptyState: {
    textAlign: "center",
    padding: "40px 16px",
    color: "#6a7d8c",
    fontFamily: "Poppins, sans-serif",
  },
  emptyTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#0c3c54",
    margin: "0 0 4px 0",
  },
  emptyHint: {
    fontSize: 12,
    margin: 0,
  },
};

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    border: state.isFocused ? "1px solid #032B4E" : "1px solid #e6eef4",
    borderRadius: 8,
    padding: "0 2px",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(3,43,78,0.12)" : "none",
    fontSize: 13,
    minHeight: 40,
    backgroundColor: "#fff",
    fontFamily: "Poppins, sans-serif",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    borderRadius: 8,
    boxShadow: "0 8px 24px rgba(12, 60, 84, 0.12)",
    border: "1px solid #e6eef4",
    overflow: "hidden",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#032B4E" : state.isFocused ? "#eff7fc" : "#fff",
    color: state.isSelected ? "#fff" : "#2c3e50",
    padding: "10px 12px",
    fontSize: 13,
    cursor: "pointer",
    fontFamily: "Poppins, sans-serif",
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: 13,
    color: "#6a7d8c",
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: 13,
    color: "#2c3e50",
  }),
};

export default function SKUSalesPersonMapPage() {
  const [SKUCategoryList, setSKUCategoryList] = useState([]);
  const [salesPersonOptions, setSalesPersonOptions] = useState([]);
  const [geoskuList, setGeoskuList] = useState([]);
  const [selectedSalesPerson, setSelectedSalesPerson] = useState(null);

  useEffect(() => {
    const init = async () => {
      let productsList = await getProductHeirarchy();
      let { level2 } = addBreadcrumbs(productsList, 2);
      setSKUCategoryList(level2);
      let _salesPersons = await getAllSalesPersons();
      if (Array.isArray(_salesPersons)) {
        let _salesPersonsOptions = _salesPersons.map((el) => ({
          label: el.sp_name,
          value: el.sp_id,
        }));
        setSalesPersonOptions(_salesPersonsOptions);
      } else {
        console.error("getAllSalesPersons returned non-array:", _salesPersons);
      }
    };
    init();
  }, []);

  const handleChangeSalesPerson = (selected) => {
    setSelectedSalesPerson(selected?.value ?? null);
  };

  const handleOnChangeCheckbox = (e) => {
    const { geo, sku } = e.target.dataset;
    const item = { geo_location: geo, sku };

    setGeoskuList((prev) => {
      const exists = prev.some(
        (i) => i.geo_location === item.geo_location && i.sku === item.sku
      );
      if (e.target.checked && !exists) return [...prev, item];
      if (!e.target.checked && exists) {
        return prev.filter(
          (i) => !(i.geo_location === item.geo_location && i.sku === item.sku)
        );
      }
      return prev;
    });
  };

  const handleOnClickSaveMapping = async () => {
    let user_id = getCookie("sub");
    if (user_id === null) {
      displayMessage("danger", "Error", "User ID not found");
      return;
    }
    if (selectedSalesPerson === null) {
      displayMessage("danger", "Invalid Input", "Please select a Sales Person");
      return;
    }
    if (geoskuList.length === 0) {
      displayMessage("danger", "Invalid Input", "Select at least one SKU");
      return;
    }
    let result = await assignSKUsToSP(user_id, selectedSalesPerson, geoskuList);
    if (result === null)
      displayMessage("danger", "Error", "Failed to assign SKU");
    else displayMessage("success", "Success", "SKUs assigned successfully");
  };

  const isChecked = (geo, sku) =>
    geoskuList.some((i) => i.geo_location === geo && i.sku === sku);

  const canSave = selectedSalesPerson && geoskuList.length > 0;

  return (
    <div className="sku-map-page" style={pageStyles.wrapper}>
      <style>{skuMapPageStyles}</style>
      <PageHeader
        title="Sales Person – SKU Mapping"
        subtitle="Assign SKUs to salespersons by geography."
      />

      <div style={pageStyles.card}>
        <h2 style={pageStyles.cardTitle}>Select Sales Person</h2>
        <div style={pageStyles.selectWrap}>
          <label style={stageFormStyles.label}>
            Salesperson <span style={{ color: "#dc2626" }}>*</span>
          </label>
          <Select
            options={salesPersonOptions}
            onChange={handleChangeSalesPerson}
            placeholder="Select a salesperson..."
            styles={customSelectStyles}
            isSearchable
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
          {selectedSalesPerson && (
            <div style={pageStyles.selectedNote}>Salesperson selected</div>
          )}
        </div>
      </div>

      <div style={pageStyles.card}>
        <h2 style={pageStyles.cardTitle}>SKU Assignment</h2>
        {SKUCategoryList && SKUCategoryList.length > 0 ? (
          <div style={pageStyles.tableWrap}>
            <table style={pageStyles.table}>
              <thead>
                <tr>
                  <th style={pageStyles.th}>SKU Name</th>
                  <th style={pageStyles.th}>Geography</th>
                  <th style={pageStyles.thLast}>Select</th>
                </tr>
              </thead>
              <tbody>
                {SKUCategoryList.map((el, index) => (
                  <tr key={index}>
                    <td style={{ ...pageStyles.td, fontWeight: 600 }}>{el.breadcrumb}</td>
                    <td style={pageStyles.td}>India</td>
                    <td style={pageStyles.tdCenter}>
                      <input
                        type="checkbox"
                        data-geo="India"
                        data-sku={el.breadcrumb}
                        checked={isChecked("India", el.breadcrumb)}
                        onChange={handleOnChangeCheckbox}
                        style={pageStyles.checkbox}
                        aria-label={`Select ${el.breadcrumb}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={pageStyles.emptyState}>
            <p style={pageStyles.emptyTitle}>No SKU categories available</p>
            <p style={pageStyles.emptyHint}>Please check your data source.</p>
          </div>
        )}
      </div>

      <div style={pageStyles.saveRow}>
        <button
          type="button"
          onClick={handleOnClickSaveMapping}
          disabled={!canSave}
          style={{
            ...pageStyles.primaryBtn,
            ...(!canSave ? pageStyles.primaryBtnDisabled : {}),
          }}
        >
          <span>Save Mapping</span>
          {geoskuList.length > 0 && (
            <span style={pageStyles.countBadge}>{geoskuList.length} selected</span>
          )}
        </button>
      </div>
    </div>
  );
}

const skuMapPageStyles = `
  .sku-map-page label {
    font-family: Poppins, sans-serif !important;
    font-size: 13px !important;
    font-weight: 600 !important;
    color: #0c3c54 !important;
    margin-bottom: 8px !important;
    text-transform: none !important;
  }

  .sku-map-page label span {
    color: #dc2626 !important;
    font-size: 13px !important;
  }

  .sku-map-page h2 {
    font-family: Poppins, sans-serif !important;
    font-size: 14px !important;
    font-weight: 600 !important;
    color: #0c3c54 !important;
  }
`;

function addBreadcrumbs(data, numberOfLevels) {
  const levels = numberOfLevels;
  const levelMaps = {};

  for (let i = 1; i <= levels; i++) {
    const currentLevelKey = `level${i}`;
    if (data[currentLevelKey]) {
      levelMaps[currentLevelKey] = Object.fromEntries(
        data[currentLevelKey].map((level) => [
          Array.from({ length: i }, (_, idx) => level[`level${idx + 1}_id`]).join("_"),
          level.name,
        ])
      );
    }
  }

  for (let targetLevel = 1; targetLevel <= levels; targetLevel++) {
    const targetLevelKey = `level${targetLevel}`;
    if (!data[targetLevelKey]) continue;

    data[targetLevelKey] = data[targetLevelKey].map((item) => {
      const breadcrumbParts = [];
      const idParts = [];

      for (let i = 1; i <= targetLevel; i++) {
        const lookupKey = Array.from({ length: i }, (_, idx) =>
          item[`level${idx + 1}_id`]
        ).join("_");
        breadcrumbParts.push(levelMaps[`level${i}`]?.[lookupKey] || item.name);
        idParts.push(item[`level${i}_id`]);
      }

      return {
        ...item,
        breadcrumb: breadcrumbParts.join(" \\ "),
        id: idParts.join("_"),
      };
    });
  }
  return data;
}
